import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CurrencyDollar, MagnifyingGlass, FileCsv, Check, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import CancelSaleDialog from "../components/CancelSaleDialog";

function fmt$(v) { return `$${Number(v || 0).toFixed(2)}`; }
function todayISO() { return new Date().toISOString().slice(0, 10); }
function isoAddDays(d, days) {
  const dt = new Date(d || todayISO());
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export default function ReceivablesPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ open: [], paid: [], open_total: 0, paid_total: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("open"); // 'open' | 'paid' | 'all'
  const [start, setStart] = useState(isoAddDays(todayISO(), -90));
  const [end, setEnd] = useState(todayISO());
  const [query, setQuery] = useState("");
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/receivables");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const all = [
      ...(data.open || []).map((r) => ({ ...r, _status: "open" })),
      ...(data.paid || []).map((r) => ({ ...r, _status: "paid" })),
    ];
    return all.filter((r) => {
      if (status !== "all" && r._status !== status) return false;
      // Date range — applied to created_at (sale date).
      if (start && (r.created_at || "") < start) return false;
      if (end && (r.created_at || "") > end + "T23:59:59") return false;
      if (query) {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        const hay = [
          r.to_user_name, r.to_user_email, r.product_name, r.note,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, status, start, end, query]);

  const filteredTotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const openInFilter = rows.filter((r) => r._status === "open");
  const selectedOpenIds = openInFilter.filter((r) => checked.has(r.id)).map((r) => r.id);
  const allOpenSelected = openInFilter.length > 0 && selectedOpenIds.length === openInFilter.length;

  const toggleAll = () => {
    if (allOpenSelected) {
      setChecked(new Set());
    } else {
      setChecked(new Set(openInFilter.map((r) => r.id)));
    }
  };

  const toggleOne = (id) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
  };

  const bulkMarkPaid = async () => {
    if (selectedOpenIds.length === 0) return;
    if (!window.confirm(t("receivables_page.confirm_bulk", { n: selectedOpenIds.length }))) return;
    setBusy(true);
    try {
      // Fire in parallel — each call independently toggles the row.
      const results = await Promise.allSettled(
        selectedOpenIds.map((id) => api.post(`/receivables/${id}/mark-paid`, {})),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok > 0) toast.success(t("receivables_page.bulk_marked", { n: ok }));
      if (fail > 0) toast.error(t("receivables_page.bulk_failed", { n: fail }));
      setChecked(new Set());
      await load();
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const header = ["Date", "Status", "Recipient", "Product", "Qty", "Unit Price", "Amount", "Note"];
    const data = rows.map((r) => [
      (r.created_at || "").slice(0, 10),
      r._status,
      r.to_user_name || r.to_user_email || "",
      r.product_name || "",
      r.quantity,
      Number(r.unit_price || 0).toFixed(2),
      Number(r.amount || 0).toFixed(2),
      (r.note || "").replace(/\n/g, " ").slice(0, 500),
    ]);
    const csv = [header, ...data].map((row) =>
      row.map((cell) => {
        const s = String(cell ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `receivables-${todayISO()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return (iso || "").slice(0, 10); }
  };

  return (
    <div className="space-y-5" data-testid="receivables-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("receivables_page.title")}</h1>
          <p className="text-sm text-zinc-600 mt-1">{t("receivables_page.subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BrutalButton variant="secondary" onClick={exportCsv} data-testid="receivables-export-csv">
            <FileCsv size={14} weight="bold" /> {t("expenses.export_csv")}
          </BrutalButton>
        </div>
      </div>

      <BrutalCard>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Tile
            label={t("receivables_page.open_total")}
            value={fmt$(data.open_total)}
            badge={`${data.open?.length || 0}`}
            tone="rose"
          />
          <Tile
            label={t("receivables_page.paid_total")}
            value={fmt$(data.paid_total)}
            badge={`${data.paid?.length || 0}`}
            tone="emerald"
          />
          <Tile
            label={t("receivables_page.shown")}
            value={fmt$(filteredTotal)}
            badge={`${rows.length}`}
            tone="zinc"
          />
          <Tile
            label={t("receivables_page.selected")}
            value={`${selectedOpenIds.length}`}
            badge={selectedOpenIds.length > 0 ? fmt$(openInFilter.filter((r) => checked.has(r.id)).reduce((s, r) => s + (r.amount || 0), 0)) : ""}
            tone="amber"
          />
        </div>
      </BrutalCard>

      <BrutalCard>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex gap-1 shrink-0" role="tablist" aria-label="status filter">
            {[
              { v: "open", label: t("receivables_page.tab_open") },
              { v: "paid", label: t("receivables_page.tab_paid") },
              { v: "all", label: t("receivables_page.tab_all") },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => { setStatus(opt.v); setChecked(new Set()); }}
                className={`h-9 px-3 rounded-md border text-sm font-medium ${
                  status === opt.v
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
                data-testid={`recv-tab-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <BrutalInput
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              data-testid="recv-start"
              className="w-40"
            />
            <span className="text-xs text-zinc-500">→</span>
            <BrutalInput
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              data-testid="recv-end"
              className="w-40"
            />
          </div>
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <BrutalInput
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("receivables_page.search_placeholder")}
              data-testid="recv-search"
              className="pl-9"
            />
          </div>
        </div>
      </BrutalCard>

      {selectedOpenIds.length > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="bulk-bar">
          <div className="text-sm text-amber-900">
            <span className="font-semibold">{selectedOpenIds.length}</span>{" "}
            {t("receivables_page.selected_open")}
          </div>
          <div className="flex gap-2">
            <BrutalButton
              size="sm"
              variant="secondary"
              onClick={() => setChecked(new Set())}
              disabled={busy}
              data-testid="bulk-clear"
            >
              {t("common.clear")}
            </BrutalButton>
            <BrutalButton
              size="sm"
              onClick={bulkMarkPaid}
              disabled={busy}
              data-testid="bulk-mark-paid"
            >
              <Check size={14} weight="bold" /> {busy ? t("receivables_page.marking") : t("receivables_page.bulk_mark_paid")}
            </BrutalButton>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <BrutalCard>
          <div className="text-sm text-zinc-600 py-2 text-center">{t("receivables_page.empty")}</div>
        </BrutalCard>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[2rem_1fr_1fr_5rem_6rem_5rem] sm:grid-cols-[2.5rem_1fr_1fr_6rem_7rem_8rem] gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            {openInFilter.length > 0 ? (
              <input
                type="checkbox"
                checked={allOpenSelected}
                onChange={toggleAll}
                aria-label="select all"
                data-testid="recv-select-all"
                className="h-4 w-4"
              />
            ) : <span />}
            <span>{t("receivables_page.col_recipient")}</span>
            <span>{t("receivables_page.col_product")}</span>
            <span className="text-right">{t("receivables_page.col_date")}</span>
            <span className="text-right">{t("receivables_page.col_amount")}</span>
            <span className="text-right">{t("receivables_page.col_actions")}</span>
          </div>
          {rows.map((r) => (
            <ReceivableRow
              key={r.id}
              row={r}
              checked={checked.has(r.id)}
              onToggle={() => toggleOne(r.id)}
              onMarkPaid={async () => {
                try {
                  await api.post(`/receivables/${r.id}/mark-paid`, {});
                  toast.success(t("receivables_page.toggled"));
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              onCancel={() => setCancelTarget(r)}
              fmtDate={fmtDate}
              t={t}
            />
          ))}
        </div>
      )}

      {cancelTarget && (
        <CancelSaleDialog
          sale={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={() => load()}
        />
      )}
    </div>
  );
}

function Tile({ label, value, badge, tone = "zinc" }) {
  const tones = {
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    zinc: "bg-zinc-50 border-zinc-200 text-zinc-900",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-xl font-bold tabular-nums">{value}</span>
        {badge && <span className="text-[11px] opacity-70">{badge}</span>}
      </div>
    </div>
  );
}

function ReceivableRow({ row, checked, onToggle, onMarkPaid, onCancel, fmtDate, t }) {
  const isPaid = row._status === "paid";
  return (
    <div
      className={`grid grid-cols-[2rem_1fr_1fr_5rem_6rem_5rem] sm:grid-cols-[2.5rem_1fr_1fr_6rem_7rem_8rem] gap-2 px-4 py-3 border-b border-zinc-100 last:border-b-0 items-center text-sm ${
        isPaid ? "bg-emerald-50/30" : ""
      }`}
      data-testid={`recv-row-${row.id}`}
    >
      {!isPaid ? (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label="select row"
          data-testid={`recv-select-${row.id}`}
          className="h-4 w-4"
        />
      ) : <Check size={14} weight="bold" className="text-emerald-600" />}
      <div className="min-w-0">
        <div className="font-semibold text-zinc-900 truncate">{row.to_user_name || row.to_user_email || "—"}</div>
        {row.note && <div className="text-[11px] italic text-zinc-500 truncate">&ldquo;{row.note}&rdquo;</div>}
      </div>
      <div className="min-w-0">
        <span className="text-zinc-900 truncate">{row.product_name}</span>
        <span className="text-zinc-500 text-xs ml-1">× {row.quantity}</span>
      </div>
      <div className="text-right text-xs text-zinc-500">{fmtDate(row.created_at)}</div>
      <div className={`text-right font-semibold tabular-nums ${isPaid ? "text-emerald-700" : "text-zinc-900"}`}>
        ${Number(row.amount || 0).toFixed(2)}
      </div>
      <div className="flex justify-end gap-1">
        <BrutalButton
          size="sm"
          variant={isPaid ? "secondary" : "primary"}
          onClick={onMarkPaid}
          data-testid={`recv-toggle-${row.id}`}
        >
          {isPaid ? t("receivables.mark_unpaid") : t("receivables.mark_paid")}
        </BrutalButton>
        {!isPaid && (
          <button
            type="button"
            onClick={onCancel}
            className="w-7 h-7 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 flex items-center justify-center"
            data-testid={`recv-cancel-${row.id}`}
            title={t("cancel_sale.button")}
          >
            <XCircle size={13} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}
